/**
 *  pegasus
 *
 *  The main pegasus JS object
 *
 *  @since	2.0.0
 *
 */
import { breakpoints } from './03-breakpoints.js';

// Initialise the global pegasus object
let pegasus = {};

// Set as a browser global
window.pegasus = pegasus;


/**
 *  breakpoints
 *
 *  Making the breakpoints available globally with utility methods
 *
 *  @since	2.0.0
 */
pegasus.breakpoints = {
    ...breakpoints,
    
    /**
     * Get numeric value of breakpoint
     * @param {string} name - Breakpoint name (sm, md, lg, xl, 2xl, 3xl)
     * @returns {number} Breakpoint value in pixels
     */
    getValue(name) {
        return parseInt(this[name], 10);
    },

    /**
     * Check if viewport is at least a specific breakpoint
     * @param {string} name - Breakpoint name
     * @returns {boolean}
     */
    isMin(name) {
        return window.innerWidth >= this.getValue(name);
    },

    /**
     * Check if viewport is below a specific breakpoint
     * @param {string} name - Breakpoint name
     * @returns {boolean}
     */
    isMax(name) {
        return window.innerWidth < this.getValue(name);
    },
}

/**
 *  getURL
 *
 *  Get the current URL of the page without any search params
 *
 *  @since	2.0.0
 *
 *  @return	current url
 */
pegasus.getURL = () => {
    return window.location.protocol + '//' + window.location.hostname + window.location.pathname;
};

/**
 *  urlWithParams
 *
 *  Add url parameters onto any url
 *
 *  @since	2.0.0
 *
 *  @param	key      Param key.
 *  @param  value    Param value.
 *  @param  location URL to add params to (Optional)
 *  @return string 	 current url with appended params
 */
pegasus.urlWithParams = (key, value, location = window.location.href) => {
    if (typeof key !== 'string' || typeof value !== 'string') {
        throw new TypeError('Key and value must be strings');
    }

    let url;
    try {
        url = new URL(location);
    } catch (error) {
        throw new Error('Invalid URL');
    }

    let params = new URLSearchParams(url.search);
    params.append(key, value);

    url.search = params.toString();

    return url.toString();
}

/**
 *  ajaxContent
 *
 *  Pull any content from another page
 *
 *  @since	2.0.0
 *
 *  @param	url       The URL to pull the content from.
 *  @param  elements  The selector(s) of the elements to grab.
 *  @param  data      Additional Ajax data to send with the request
 *  @param  callback  A function to run after the ajax request has completed.
 *  @return mixed
 */
pegasus.ajaxContent = (url, elements = [], data = {}, callback) => {

    pegasus.fetchContent(url, data)
        .then(resp => {
            if (!resp.ok) {
                throw Error(resp.statusText);
            }
            return resp.text();
        })
        .then(text => {
            pegasus.ajaxReplaceContent(text, elements);
        })
        .then(() => {
            if (typeof callback !== 'undefined') {
                callback();
            }
        })
        .catch(error => {
            console.error(error);
            if (typeof callback !== 'undefined') {
                callback();
            }
        });

    return this;
}

/**
 *  loadMoreContent
 *
 *  Pull any content from another page and load it beneath the selected component
 *
 *  @since	2.0.0
 *
 *  @param	url        The URL to pull the content from.
 *  @param  container  The container to add the new content into.
 *  @param  elements   The selector(s) of the elements to grab.
 *  @param  data       Additional Ajax data to send with the request
 *  @param  callback   A function to run after the ajax request has completed.
 *  @return mixed
 */
pegasus.loadMoreContent = (url, container, elements = [], data = {}, callback) => {

    pegasus.fetchContent(url, data)
        .then(resp => {
            if (!resp.ok) {
                throw Error(resp.statusText);
            }
            return resp.text();
        })
        .then(text => {
            const parser = new DOMParser(),
                doc = parser.parseFromString(text, 'text/html');

            elements.map(elem => {
                const find = document.querySelector(container),
                    append = doc.querySelectorAll(`${container} ${elem}`);

                append.forEach(el => {
                    find.appendChild(el);
                })
            });
        })
        .then(() => {
            // Re-initialize lazy loader to pick up new assets
            if (window.PegasusLazyLoaderInstance && typeof window.PegasusLazyLoaderInstance.init === 'function') {
                window.PegasusLazyLoaderInstance.init();
            } else if (typeof PegasusLazyLoader !== 'undefined') {
                window.PegasusLazyLoaderInstance = new PegasusLazyLoader();
            }

            if (typeof callback !== 'undefined') {
                callback();
            }
        })
        .catch(error => {
            console.error(error);
            if (typeof callback !== 'undefined') {
                callback();
            }
        });
}

/**
 *  fetchContent
 *
 *  Returns an ajax fetch request
 *
 *  @since	2.0.0
 *
 *  @param	url       The URL to fetch from.
 *  @param  data      Additional Ajax data to send with the request
 *  @return object 	  Ajax response data
 */
pegasus._lastFetchRequest = {
    url: null,
    dataString: null,
    promise: null
};

pegasus.fetchContent = async (url, data = {}) => {
    // Serialize data for comparison (order-insensitive)
    const dataString = JSON.stringify(Object.keys(data).sort().reduce((obj, key) => {
        obj[key] = data[key];
        return obj;
    }, {}));

    // If the last request matches, return the same promise (prevents duplicate fetches)
    if (
        pegasus._lastFetchRequest.url === url &&
        pegasus._lastFetchRequest.dataString === dataString &&
        pegasus._lastFetchRequest.promise
    ) {
        return pegasus._lastFetchRequest.promise;
    }

    // Otherwise, make a new request and cache it
    const fetchPromise = fetch(url, pegasus.setAjaxData('GET', data));
    pegasus._lastFetchRequest = {
        url,
        dataString,
        promise: fetchPromise
    };
    // When the promise settles, clear the cache if it matches
    fetchPromise.finally(() => {
        if (
            pegasus._lastFetchRequest.url === url &&
            pegasus._lastFetchRequest.dataString === dataString
        ) {
            pegasus._lastFetchRequest = { url: null, dataString: null, promise: null };
        }
    });
    return fetchPromise;
}

/**
 *  setAjaxData
 *
 *  Returns an object of Ajax headers
 *
 *  @since	2.0.0
 *
 *  @param	method    The request method required
 *  @param  data      Additional data to merge with the default headers
 *  @return object 	  Ajax request headers
 */
pegasus.setAjaxData = (method, data) => {

    const defaults = {
        method: `${method}`,
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        },
    }

    return pegasus.extend(defaults, data);
}

/**
 *  ajaxReplaceContent
 *
 *  Replaces current DOM elements with elements from a given html document
 *
 *  @since	2.0.0
 *
 *  @param	html      The html document provided as a string
 *  @param  elements  An array of element selectors to overwrite
 *  @return mixed
 */
pegasus.ajaxReplaceContent = (html, elements) => {

    const parser = new DOMParser(),
        doc = parser.parseFromString(html, 'text/html');

    elements.forEach(elem => {
        // Find all matching elements in current document
        const findElements = document.querySelectorAll("["+elem+"]");
        
        // Find all matching elements in fetched HTML
        const replaceElements = doc.querySelectorAll("["+elem+"]");
        
        // Replace each found element with its corresponding element from fetched HTML
        findElements.forEach((findElem, index) => {
            // Make sure we have a matching element to replace with
            if (replaceElements[index]) {
                findElem.parentNode.replaceChild(replaceElements[index], findElem);
            }
        });

    });


    // Re-initialize lazy loader to pick up new assets
    if (window.PegasusLazyLoaderInstance && typeof window.PegasusLazyLoaderInstance.init === 'function') {
        window.PegasusLazyLoaderInstance.init();
    } else if (typeof PegasusLazyLoader !== 'undefined') {
        // fallback: create instance if not present
        window.PegasusLazyLoaderInstance = new PegasusLazyLoader();
    }

    // Re-initialize modal to pick up new assets
    if(typeof window.modal !== "undefined"){
        window.modal.init();
    }

    return this
}

/**
 *  extend
 *
 *  Replicates the jQuery $.extend functionality and merges multiple objects.
 *
 *  @since	2.0.0
 *
 *  @return object
 */
pegasus.extend = function(){
    // Variables
    let extended = {};
    let deep = false;
    let i = 0;
    let length = arguments.length;

    // Check if a deep merge
    if (Object.prototype.toString.call( arguments[0] ) === '[object Boolean]') {
        deep = arguments[0];
        i++;
    }

    // Merge the object into the extended object
    let merge = function (obj) {
        for ( let prop in obj ) {
            if ( Object.prototype.hasOwnProperty.call( obj, prop ) ) {
                // If deep merge and property is an object, merge properties
                if ( deep && Object.prototype.toString.call(obj[prop]) === '[object Object]' ) {
                    extended[prop] = pegasus.extend( true, extended[prop], obj[prop] );
                } else {
                    extended[prop] = obj[prop];
                }
            }
        }
    };

    // Loop through each object and conduct a merge
    for ( ; i < length; i++ ) {
        let obj = arguments[i];
        merge(obj);
    }

    return extended;

}

/**
 *  onResizeFinished
 *
 *  Add window resize listener, with timeout throttle and onLoad call
 *  Enhanced with ResizeObserver support for better performance
 *
 *  @since	2.0.0
 *
 *  @param	callback      The function to run
 *  @param  runOnLoad     Boolean, whether to run on page load
 *  @param  options       Optional configuration object
 *                        - element: HTMLElement to observe (defaults to window)
 *                        - debounceTime: Number of ms to debounce (defaults to 50)
 *                        - useResizeObserver: Boolean to prefer ResizeObserver (defaults to true)
 */
pegasus.onResizeFinished = (callback, runOnLoad, options = {}) => {
    const {
        element = null,
        debounceTime = 50,
        useResizeObserver = true
    } = options;

    let resizeTimer;
    let previousWidth = window.innerWidth;
    let observer;

    const executeCallback = () => {
        const currentWidth = element ? element.clientWidth : window.innerWidth;
        if (currentWidth !== previousWidth) {
            previousWidth = currentWidth;
            callback();
        }
    };

    const debouncedCallback = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(executeCallback, debounceTime);
    };

    // Use ResizeObserver if available and requested, and we have a specific element
    if (useResizeObserver && typeof ResizeObserver !== 'undefined' && element) {
        observer = new ResizeObserver(debouncedCallback);
        observer.observe(element);
    } else {
        // Fallback to window resize event (original behavior)
        window.addEventListener('resize', debouncedCallback);
    }

    if (runOnLoad) {
        callback.call();
    }

    // Return cleanup function
    return () => {
        clearTimeout(resizeTimer);
        if (observer) {
            observer.disconnect();
        } else {
            window.removeEventListener('resize', debouncedCallback);
        }
    };
};

/**
 *  scroll
 *
 *  Scroll helper to control scrolling to certain areas on the page when needed.
 *
 *  @since	2.0.0
 *
 *  @param	target            The element to base the scrolling behaviour around.
 *  @param  additionalOffset  The additional offset to apply to the scroll position
 *  @param  behaviour         Where in the document should be scrolled to in relation to the target scrollTo | scrollPast
 *  @return mixed
 */
pegasus.scroll = (target, additionalOffset = 0, behaviour = 'scrollTo') => {
    if(!target || !target instanceof HTMLElement){
        throw new Error('The target element must be an HTML element.');
    }

    const rect = target.getBoundingClientRect(),
        scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    let offset = 0;

    if(behaviour === 'scrollTo'){
        offset = rect.top + scrollTop + additionalOffset;
    }

    if(behaviour === 'scrollPast'){
        offset = (rect.top + target.offsetHeight) + scrollTop + additionalOffset;
    }

    let scrollY = offset;

    if(scrollY > document.body.offsetHeight){
        scrollY = document.body.offsetHeight;
    } else if(scrollY < 0){
        scrollY = 0;
    }

    window.scrollTo({
        top: scrollY,
        behavior: 'smooth'
    });

}

/**
 *  setCookie
 *
 *  Sets a browser cookie
 *
 *  @since	2.0.0
 *
 *  @param	name     Sets cookie name
 *  @param	value    Sets cookie value
 *  @param	exdays    Sets cookie expiry in days
 */
pegasus.setCookie = (name, value, exdays) => {

    const d = new Date();

    d.setTime(d.getTime() + (exdays*24*60*60*1000));

    let expires = "expires="+ d.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";

};

/**
 *  getCookie
 *
 *  Gets a browser cookie
 *
 *  @since	2.0.0
 *
 *  @param	name     Get cookie by name
 *  @return string   Returns value of cookie or empty string
 */
pegasus.getCookie = (name) => {

    let cookieName = name + "=";

    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');

    for(let i = 0; i <ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(cookieName) == 0) {
            return c.substring(cookieName.length, c.length);
        }
    }
    return "";

};

/**
 * stringToHTML
 *
 * Converts a template string into HTML DOM nodes
 *
 * @since  2.0.0

 * @param  string     The template string
 * @return node       The template HTML
 */
pegasus.stringToHTML = function (str) {

    const support = (function () {
        if (!window.DOMParser) return false;
        const parser = new DOMParser();
        try {
            parser.parseFromString('x', 'text/html');
        } catch(err) {
            return false;
        }
        return true;
    })();

    // If DOMParser is supported, use it
    if (support) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(str, 'text/html');
        return doc.body.firstChild;
    }

    // Otherwise, fallback to old-school method
    const dom = document.createElement('div');
    dom.innerHTML = str;
    return dom;

};

/**
 * getElemOffset
 *
 * Returns the top, bottom, and left position of the given element
 *
 * @since  2.0.0

 * @param  node     The element to get the offset of
 * @return object   Object containing top, bottom, and left positions
 */

pegasus.getElemOffset = (el) => {
    if (!el || !el.getBoundingClientRect) return null;
    const rect = el.getBoundingClientRect(),
        scrollLeft = window.scrollX || document.documentElement.scrollLeft,
        scrollTop = window.scrollY || document.documentElement.scrollTop;

    return { top: rect.top + scrollTop, bottom: (rect.top + scrollTop) + rect.height, left: rect.left + scrollLeft }
};

/**
 * decodeAllCloudflareEmails
 *
 * Grabs all encoded email links and decodes them
 *
 * @since  2.0.0
 * @param  node     The element to search for encoded emails
 * @return void
 */
pegasus.decodeAllCloudflareEmails = (node = document) => {
    const emailLinks = node.querySelectorAll('a[href^="/cdn-cgi/l/email-protection#"]');

    emailLinks.forEach(function(link) {
        const encoded = link.href.split('#').pop() || link.href.split('protection#').pop() || link.href.split('protection').pop();
        if (encoded) {
            const email = pegasus.decodeCloudflareEmail(encoded);
            link.href = 'mailto:' + email;
            link.textContent = email;
        }
    });
};

/**
 * decodeCloudflareEmail
 *
 * Helper function to decode Cloudflare encoded emails
 *
 * @since  2.0.0
 * @param  string     The encoded email string
 * @return void
 */
pegasus.decodeCloudflareEmail = (encoded) =>{
    try {
        let email = '';
        const r = parseInt(encoded.substring(0, 2), 16);
        for (let n = 2; n < encoded.length; n += 2) {
            const i = parseInt(encoded.substring(n, n + 2), 16) ^ r;
            email += String.fromCharCode(i);
        }
        return email;
    } catch (e) {
        console.error('Failed to decode email:', e);
        return '';
    }
};

/**
 * wait
 *
 * Promise based wait function for clearner delay
 *
 * @since  2.0.0

 * @param  int     milliseconds   The number of milliseconds to wait
 * @return {Promise}   Promise that resolves after the given time
 */
pegasus.wait = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/** * parseJsonOptions
 *
 * Parses JSON options passed as a string or returns the object directly
 *
 * @since  2.0.0
 * @param  options   The options to parse
 * @return object    Parsed options object
 */
pegasus.parseJsonOptions = (options) => {
    if (typeof options === 'string') {
        try {
            return JSON.parse(options);
        } catch (e) {
            console.error("Failed to parse options:", e);
            return {};
        }
    }
    return options;
};


/** * waitForEditorReady
 * Waits for the WordPress editor to be ready before executing a callback
 * Tries for up to 2 seconds for wp.domReady, then falls back to setTimeout
 * @since  2.0.0
 * @return {Promise<void>}
 */
pegasus.waitForEditorReady = function () {
    return new Promise((resolve) => {
        let resolved = false;
        function checkWpDomReady() {
            if (window.wp && typeof window.wp.domReady === 'function') {
                window.wp.domReady(() => {
                    if (!resolved) {
                        resolved = true;
                        clearInterval(interval);
                        clearTimeout(fallbackTimeout);
                        resolve();
                    }
                });
            }
        }
        // Otherwise, poll every 50ms until available
        const interval = setInterval(checkWpDomReady, 50);
        // Fallback: after 2 seconds, resolve anyway
        const fallbackTimeout = setTimeout(() => {
            if (!resolved) {
                resolved = true;
                clearInterval(interval);
                resolve();
            }
        }, 2000);
    });
};

/** * initFooterLinkColumns
 * Handles mobile accordion groups with group block override
 * @since  2.0.0
 * @author
 */
pegasus.initFooterLinkColumns = () => {
    const mobileAccordionGroups = document.querySelectorAll('[data-mobile-accordion="true"]');
    
    if (mobileAccordionGroups && mobileAccordionGroups.length) {
        const setupMobileAccordion = (group) => {
            const heading = group.querySelector(':scope > .wp-block-heading');
            const contentGroup = group.querySelector(':scope > .wp-block-group');
            
            if (!heading || !contentGroup) {
                return;
            }

            // Store original styles for cleanup
            if (!group.dataset.originalSetup) {
                group.dataset.originalHeadingStyle = heading.getAttribute('style') || '';
                group.dataset.originalContentStyle = contentGroup.getAttribute('style') || '';
                group.dataset.originalGroupStyle = group.getAttribute('style') || '';
                group.dataset.originalSetup = 'true';
            }

            const getContentHeight = (content) => {
                // Temporarily set height to auto to measure actual content
                const originalHeight = content.style.height;
                const originalOverflow = content.style.overflow;
                content.style.height = 'auto';
                content.style.overflow = 'visible';
                
                const height = content.scrollHeight;
                
                // Restore original styles
                content.style.height = originalHeight;
                content.style.overflow = originalOverflow;
                
                return height + 10; // Add small buffer
            };

            // Clean up any existing plus/minus indicators
            const existingIndicator = heading.querySelector('.accordion-indicator');
            if (existingIndicator) {
                existingIndicator.remove();
            }

            // Reset to original state first
            heading.style.cssText = group.dataset.originalHeadingStyle;
            contentGroup.style.cssText = group.dataset.originalContentStyle;
            group.style.cssText = group.dataset.originalGroupStyle;

            // Remove event listeners by cloning (clean slate)
            const newHeading = heading.cloneNode(true);
            heading.parentNode.replaceChild(newHeading, heading);

            if (pegasus.breakpoints.isMin('lg')) {
                // Desktop: Normal display, no accordion behavior
                contentGroup.style.height = 'auto';
                contentGroup.style.overflow = 'visible';
                newHeading.style.cursor = 'default';                
            } else {
                // Mobile: Accordion behavior
                // Add consistent styling for accordion groups
                group.style.borderTop = '1px solid rgba(0,0,0,0.1)';
                group.style.paddingTop = '1rem';
                group.style.paddingBottom = '1rem';
                group.style.width = "100%";
                
                // Reset heading styles for mobile
                newHeading.style.margin = '0';
                newHeading.style.padding = '0';
                newHeading.style.cursor = 'pointer';
                
                // Create plus/minus indicator
                const indicator = document.createElement('span');
                indicator.className = 'accordion-indicator';
                indicator.innerHTML = '+';
                indicator.style.cssText = `
                    display: inline-block;
                    transition: all 0.3s ease;
                    font-size: 1.2em;
                    color: #808791;
                    margin-left: auto;
                    font-weight: bold;
                    line-height: 1;
                `;
                
                // Style the heading as flex container
                newHeading.style.cssText += `
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                `;
                
                // Append indicator to heading
                newHeading.appendChild(indicator);
                
                // Set up content group for accordion
                contentGroup.style.overflow = 'hidden';
                contentGroup.style.transition = 'height 0.3s ease, margin-top 0.3s ease';
                contentGroup.style.height = '0px';
                contentGroup.style.marginTop = '0px';
                
                // Remove any accordion open states
                newHeading.classList.remove('-accordion-open');
                
                // Add click event for mobile accordion
                newHeading.addEventListener("click", (e) => {
                    e.preventDefault();
                    const height = getContentHeight(contentGroup);
                    const indicator = newHeading.querySelector('.accordion-indicator');

                    if (newHeading.classList.contains("-accordion-open")) {
                        // Close accordion
                        contentGroup.style.height = "0px";
                        contentGroup.style.marginTop = "0px";
                        newHeading.classList.remove("-accordion-open");
                        if (indicator) {
                            indicator.innerHTML = '+';
                        }
                    } else {
                        // Open accordion
                        contentGroup.style.height = height + "px";
                        contentGroup.style.marginTop = "1rem";
                        newHeading.classList.add("-accordion-open");
                        if (indicator) {
                            indicator.innerHTML = '−';
                        }
                    }
                });
            }
        };

        mobileAccordionGroups.forEach(setupMobileAccordion);
        
        // Re-setup on window resize to handle desktop/mobile transitions
        pegasus.onResizeFinished(() => {
            mobileAccordionGroups.forEach(setupMobileAccordion);
        });
    }
};

export default pegasus;